import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { FaFacebook, FaInstagram, FaWhatsapp, FaTiktok } from 'react-icons/fa';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const socialLinks = [
    { icon: FaFacebook, href: "http://facebook.com/Bienestaralnaturalmedellin", color: "text-blue-600 hover:text-blue-800" },
    { icon: FaInstagram, href: "https://www.instagram.com/bienestaralnatural.medellin/", color: "text-pink-500 hover:text-pink-700" },
    { icon: FaWhatsapp, href: `https://wa.me/573237076868?text=${encodeURIComponent("Hola, los vi en su website obtener mas información sobre sus servicios")}`, color: "text-green-500 hover:text-green-700" },
    { icon: FaTiktok, href: "https://www.tiktok.com/@bienestar.al.natu2?is_from_webapp=1&sender_device=pc", color: "text-gray-900 hover:text-black" }
  ];

  const menuItems = [
    { name: 'Inicio', href: '#home' },
    { name: 'Nosotros', href: '#about' },
    { name: 'Servicios', href: '#services' },
    { name: 'Galería', href: '#gallery' },
    { name: 'Contacto', href: '#contact' }
  ];

  const handleScroll = (target) => {
    const section = document.querySelector(target);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMenuOpen(false);
  };

  return (
    <motion.header
      className="bg-green-600 shadow-xl fixed w-full top-0 z-[1000] py-2 lg:py-4"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between flex-wrap">
          {/* Logo */}
          <motion.div className="flex items-center gap-2 lg:gap-4 mb-1 lg:mb-0" whileHover={{ scale: 1.05 }}>
            <motion.img
              src="https://utfs.io/f/5BN0V4mlt4NUrOWP0qz5NPcaAgwizpdKqn4rb3Q1IHGDuV67"
              alt="Bienestar Al Natural Logo"
              className="w-10 h-10 lg:w-24 lg:h-24 shadow-lg object-cover flex-shrink-0"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            />
            <div className="flex flex-col min-w-0">
              <h1 className="text-base lg:text-xl xl:text-2xl font-bold text-white leading-snug truncate">
                Bienestar al Natural
              </h1>
              <p className="text-xs lg:text-base font-bold text-white leading-snug truncate">
                Tu Centro de Salud y Belleza
              </p>
            </div>
          </motion.div>

          {/* Desktop Menu */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-10">
            {menuItems.map((item, index) => (
              <motion.a
                key={item.name}
                href={item.href}
                onClick={() => handleScroll(item.href)}
                className="text-base lg:text-xl xl:text-2xl font-bold text-white truncate cursor-pointer"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
              >
                {item.name}
              </motion.a>
            ))}
          </nav>

          {/* Social Links */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3 xl:gap-4">
            {socialLinks.map((social, index) => (
              <motion.a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 lg:p-3 rounded-full bg-gray-100 hover:bg-green-50 transition-all duration-300 ${social.color} shadow-md hover:shadow-lg flex-shrink-0`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <social.icon className="w-4 h-4 lg:w-5 lg:h-5" />
              </motion.a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden p-3 rounded-lg bg-gray-100 shadow-md flex-shrink-0"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          className={`lg:hidden overflow-hidden ${isMenuOpen ? 'max-h-[500px]' : 'max-h-0'} transition-all duration-300 ease-in-out`}
        >
          <div className="py-3 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-4">
            <div className="space-y-2 mb-4">
              {menuItems.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  onClick={() => handleScroll(item.href)}
                  className="block py-2 px-3 text-gray-700 hover:text-green-600 hover:bg-green-50 font-semibold text-sm rounded-lg transition-all duration-300 cursor-pointer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, x: 5 }}
                >
                  {item.name}
                </motion.a>
              ))}
            </div>
            <div className="flex gap-3 pb-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-3 rounded-full bg-gray-100 ${social.color} shadow-md hover:shadow-lg transition-all duration-300 flex-shrink-0 w-10 h-10 flex items-center justify-center`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  whileHover={{ scale: 1.1 }}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header;


